import {
  AdminOrganisationResponse,
  AdminTeamResponse,
  AdminUpdateQuotasResponse,
  Invitation,
  Organisation,
  SubscriptionStatus,
  UserWithRole,
} from '@fsp/types';
import AssignmentTwoTone from '@mui/icons-material/AssignmentTwoTone';
import BusinessTwoTone from '@mui/icons-material/BusinessTwoTone';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionTwoTone from '@mui/icons-material/DescriptionTwoTone';
import GroupTwoTone from '@mui/icons-material/GroupTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistAddTwoTone from '@mui/icons-material/PlaylistAddTwoTone';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import TuneIcon from '@mui/icons-material/Tune';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BreadCrumb from '../../../components/BreadCrumb';
import UserChip from '../../../components/UserChip';
import ActivityList from '../../../components/ActivityList';
import PaperSection from '../../../components/PaperSection';
import getRoleColor from '../../../helpers/getRoleColor';
import getStatusColor from '../../../helpers/getStatusColor';
import { apiClient } from '../../../services/apiClient';
import { formatDate, DateFormatOptions } from '@fsp/shared';
import { getSubscriptionStatusColor } from '../../../utils/getSubscriptionStatusColor';
import { useSnackbar } from 'notistack';

export default function OrganisationView() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<UserWithRole[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [totalPlans, setTotalPlans] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Quota state
  const [quotaMaxTeamMembers, setQuotaMaxTeamMembers] = useState('1');
  const [quotaMaxPlans, setQuotaMaxPlans] = useState('1');
  const [quotaAiRequests, setQuotaAiRequests] = useState('0');
  const [quotaAiRequestsUsed, setQuotaAiRequestsUsed] = useState('0');
  const [quotaSaving, setQuotaSaving] = useState(false);

  // Trial state
  const [trialExtending, setTrialExtending] = useState(false);

  // Affiliate state
  const [affiliateName, setAffiliateName] = useState<string | null>(null);
  const [affiliateAccountId, setAffiliateAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (organisationId) {
      fetchOrganisation();
      fetchTeam();
    }
  }, [organisationId]);

  const fetchOrganisation = async () => {
    if (!organisationId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/organisations/${organisationId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch organisation details');
      }

      const data: AdminOrganisationResponse = await response.json();

      console.log(data);
      setOrganisation(data.organisation);
      setQuotaMaxTeamMembers(String(data.organisation.quotaMaxTeamMembers ?? 1));
      setQuotaMaxPlans(String(data.organisation.quotaMaxPlans ?? 1));
      setQuotaAiRequests(String(data.organisation.quotaAiRequests ?? 0));
      setQuotaAiRequestsUsed(String(data.organisation.quotaAiRequestsUsed ?? 0));
      setTotalPlans(data.totalPlans || 0);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(data.totalPages || 0);
      setAffiliateName(data.affiliateName ?? null);
      setAffiliateAccountId(data.affiliateAccountId ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching organisation:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    if (!organisationId) return;

    setTeamLoading(true);
    setTeamError(null);
    try {
      const response = await apiClient.get(`/admin/team/organisation/${organisationId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch team');
      }

      const data: AdminTeamResponse = await response.json();
      setTeamMembers(data.users || []);
      setInvitations(data.invitations || []);
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching team:', err);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleDeleteOrganisation = async () => {
    if (confirmText !== 'DELETE') {
      setDeleteError('Please type "DELETE" to confirm');
      return;
    }

    if (!organisationId) {
      setDeleteError('Organisation ID is missing');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await apiClient.delete(`/admin/organisations/${organisationId}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete organisation');
      }

      navigate('/app/organisations');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'An error occurred while deleting');
      setIsDeleting(false);
    }
  };

  const handleSaveQuotas = async () => {
    if (!organisationId) return;
    setQuotaSaving(true);
    try {
      const response = await apiClient.put(`/admin/organisations/${organisationId}/quotas`, {
        quotaMaxTeamMembers: parseInt(quotaMaxTeamMembers, 10) || 0,
        quotaMaxPlans: parseInt(quotaMaxPlans, 10) || 0,
        quotaAiRequests: parseInt(quotaAiRequests, 10) || 0,
        quotaAiRequestsUsed: parseInt(quotaAiRequestsUsed, 10) || 0,
      });
      if (!response.ok) throw new Error('Failed to update quotas');
      const data: AdminUpdateQuotasResponse = await response.json();
      setOrganisation(data.organisation);
      enqueueSnackbar('Quotas updated successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error saving quotas:', err);
    } finally {
      setQuotaSaving(false);
    }
  };

  const handleExtendTrial = async (days: number) => {
    if (!organisationId) return;
    setTrialExtending(true);
    try {
      const currentExpiry = organisation?.freeTrialExpiresAt
        ? new Date(organisation.freeTrialExpiresAt)
        : new Date();
      const baseDate = currentExpiry > new Date() ? currentExpiry : new Date();
      const newExpiry = new Date(baseDate.getTime() + days * 24 * 60 * 60 * 1000);

      const response = await apiClient.put(`/admin/organisations/${organisationId}/trial`, {
        freeTrialExpiresAt: newExpiry.toISOString(),
      });
      if (!response.ok) throw new Error('Failed to extend trial');
      const data = await response.json();
      setOrganisation(data.organisation);
      enqueueSnackbar(`Trial extended by ${days} days`, { variant: 'success' });
    } catch (err) {
      console.error('Error extending trial:', err);
      enqueueSnackbar('Failed to extend trial', { variant: 'error' });
    } finally {
      setTrialExtending(false);
    }
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setConfirmText('');
    setDeleteError(null);
  };

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const StatCard = ({
    icon,
    label,
    value,
    color = 'primary.main',
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    color?: string;
  }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !organisation) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <BreadCrumb />
          <Typography variant="h5" color="error">
            {error || 'Organisation not found'}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <BreadCrumb customLabel={organisation.name} />

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
            <BusinessTwoTone sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {organisation.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created {formatDate(organisation.createdAt, DateFormatOptions.DATEONLY)}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={organisation.subscriptionStatus}
            color={getSubscriptionStatusColor(organisation.subscriptionStatus)}
            sx={{ height: 32, fontSize: '0.875rem' }}
          />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={openDeleteDialog}
            disabled={isDeleting}
          >
            Delete
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<GroupTwoTone />} label="Team Members" value={`${teamMembers.length}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<AssignmentTwoTone />} label="Total Plans" value={totalPlans} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<DescriptionTwoTone />} label="Pages" value={totalPages} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<PlaylistAddTwoTone />} label="Records" value={totalRecords} />
        </Grid>
      </Grid>

      {/* Main Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={4}>
            {/* Organization & Contact */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Organization & Contact
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="Legal Name" value={organisation.legalName} />
                <InfoItem label="ABN" value={organisation.abn} />
                <InfoItem label="Contact Number" value={organisation.contactNumber} />
                <InfoItem label="Website" value={organisation.website} />
              </Stack>
            </Grid>

            {/* Subscription & Billing */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Subscription & Billing
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="Billing Email" value={organisation.billingEmail} />
                <InfoItem
                  label="Valid Until"
                  value={formatDate(
                    organisation.subscriptionValidUntil,
                    DateFormatOptions.DATEONLY
                  )}
                />
                <InfoItem
                  label="Card Expires"
                  value={formatDate(organisation.cardValidUntil, DateFormatOptions.DATEONLY)}
                />
                <InfoItem
                  label="Stripe Customer ID"
                  value={
                    organisation.stripeCustomerId ? (
                      <Link
                        href={`https://dashboard.stripe.com/customers/${organisation.stripeCustomerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {organisation.stripeCustomerId}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : (
                      'N/A'
                    )
                  }
                />
                {organisation.promotionCode && (
                  <InfoItem
                    label="Promotion Code Used"
                    value={
                      <Chip
                        label={organisation.promotionCode}
                        color="primary"
                        size="small"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    }
                  />
                )}
                <InfoItem
                  label="Free Trial Expires"
                  value={
                    organisation.freeTrialExpiresAt ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={
                            new Date(organisation.freeTrialExpiresAt) > new Date()
                              ? `Active — ${formatDate(organisation.freeTrialExpiresAt, DateFormatOptions.DATEONLY)}`
                              : `Expired — ${formatDate(organisation.freeTrialExpiresAt, DateFormatOptions.DATEONLY)}`
                          }
                          color={
                            new Date(organisation.freeTrialExpiresAt) > new Date()
                              ? 'success'
                              : 'error'
                          }
                          size="small"
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={trialExtending}
                          onClick={() => handleExtendTrial(30)}
                        >
                          {trialExtending ? 'Extending...' : '+30 days'}
                        </Button>
                      </Stack>
                    ) : (
                      'No trial'
                    )
                  }
                />
              </Stack>
            </Grid>

            {/* Setup Information */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Setup Information
              </Typography>
              <Stack spacing={2}>
                <InfoItem
                  label="Onboarding"
                  value={
                    <Chip
                      label={organisation.completedOnboarding ? 'Complete' : 'Incomplete'}
                      color={organisation.completedOnboarding ? 'success' : 'warning'}
                      size="small"
                    />
                  }
                />
                <InfoItem
                  label="Affiliate"
                  value={
                    affiliateName && affiliateAccountId ? (
                      <Link
                        href={`/app/auditors/${affiliateAccountId}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/app/auditors/${affiliateAccountId}`);
                        }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {affiliateName}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : (
                      'None'
                    )
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quota Management */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TuneIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Plan Quotas
            </Typography>
          </Stack>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Max Team Members"
                size="small"
                fullWidth
                value={quotaMaxTeamMembers}
                onChange={(e) => setQuotaMaxTeamMembers(e.target.value)}
                helperText="-1 = unlimited"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Max Plans"
                size="small"
                fullWidth
                value={quotaMaxPlans}
                onChange={(e) => setQuotaMaxPlans(e.target.value)}
                helperText="-1 = unlimited"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="AI Requests"
                size="small"
                fullWidth
                value={quotaAiRequests}
                onChange={(e) => setQuotaAiRequests(e.target.value)}
                helperText={`-1 = unlimited, 0 = disabled. Used: ${quotaAiRequestsUsed}`}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Stack spacing={1}>
                <Button
                  variant="contained"
                  startIcon={quotaSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveQuotas}
                  disabled={quotaSaving}
                >
                  {quotaSaving ? 'Saving...' : 'Save Quotas'}
                </Button>
                {parseInt(quotaAiRequestsUsed, 10) > 0 && (
                  <Button variant="outlined" size="small" onClick={() => setQuotaAiRequestsUsed('0')}>
                    Reset AI Usage
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users card */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Team (${teamMembers.length})`} />
          <Tab label={`Invitations (${invitations.length})`} />
        </Tabs>

        {/* Team Tab */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            {teamLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : teamError ? (
              <Typography color="error">{teamError}</Typography>
            ) : teamMembers.length === 0 ? (
              <Typography color="text.secondary">No active team members</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow
                        key={member.id}
                        onClick={() => navigate(`/app/users/${member.id}`)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            cursor: 'pointer',
                          },
                        }}
                      >
                        <TableCell>
                          <UserChip user={member} clickable={false} />
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={member.role}
                            size="small"
                            color={getRoleColor(member.role)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.status}
                            size="small"
                            sx={{ backgroundColor: getStatusColor(member.status) }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(member.joinedAt, DateFormatOptions.DATEONLY)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Invitations Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            {teamLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : teamError ? (
              <Typography color="error">{teamError}</Typography>
            ) : invitations.length === 0 ? (
              <Typography color="text.secondary">No pending invitations</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Invited By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell>{invitation.email}</TableCell>
                        <TableCell>
                          <Chip label={invitation.role} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Chip label={invitation.status} size="small" color="warning" />
                        </TableCell>
                        <TableCell>
                          {formatDate(invitation.expiresAt, DateFormatOptions.DATEONLY)}
                        </TableCell>
                        <TableCell>{invitation.invitedBy || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Card>

      {/* Activity Card */}
      <PaperSection title="Activity">
        <ActivityList
          endpoint={`/admin/activities/organisation/${organisationId}`}
          showUsers={true}
          showOrganisations={false}
        />
      </PaperSection>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Organisation</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              This action will permanently delete <strong>{organisation.name}</strong> and all
              associated data, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <li>All team members ({teamMembers.length} users)</li>
              <li>All plans ({totalPlans} plans)</li>
              <li>All pages ({totalPages} pages)</li>
              <li>All records ({totalRecords} records)</li>
              <li>All invitations and notifications</li>
              <li>All associated user accounts that only belong to this organisation</li>
            </Box>
            <Alert severity="warning">
              This action cannot be undone. Please be certain before proceeding.
            </Alert>
            <TextField
              fullWidth
              name="deleteConfirmation"
              id="deleteConfirmation"
              label='Type "DELETE" to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isDeleting}
            />
            {deleteError && (
              <Alert severity="error" variant="filled">
                {deleteError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrganisation}
            color="error"
            variant="contained"
            disabled={confirmText !== 'DELETE' || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Organisation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
